/**
 * Superset of the build-related fields either portal reads. The admin client
 * row carries the raw columns (and derives in-progress locally); the client
 * portal receives the server-derived `build_in_progress` from /tenant/status.
 * deriveBuildState handles both.
 */
export interface BuildStatusInput {
  build_in_progress?: boolean;
  build_dispatched_at?: string | null;
  last_build_at?: string | null;
  last_build_status?: string | null;
  last_deploy_url?: string | null;
  site_url?: string | null;
  pages_project_name?: string | null;
}

export interface BuildState {
  state: "building" | "published" | "failed" | "none";
  inProgress: boolean;
  label: string;
  /** Matches the shared Badge component's variant union. */
  badgeVariant: "success" | "warning" | "destructive" | "secondary";
  at: string | null;
  deployUrl: string | null;
}

/**
 * Whether a build is mid-flight. Prefers the server-derived flag when present;
 * otherwise derives it from the raw columns (dispatched more recently than the
 * last completion, or none completed yet). Mirrors buildInProgress in the API
 * (apps/api/src/tenant.ts).
 */
export function buildInProgress(input: BuildStatusInput | null | undefined): boolean {
  if (typeof input?.build_in_progress === "boolean") return input.build_in_progress;
  if (!input?.build_dispatched_at) return false;
  if (!input?.last_build_at) return true;
  return new Date(input.build_dispatched_at) > new Date(input.last_build_at);
}

/**
 * Display state for a site build. GitHub Actions exposes no granular progress,
 * so this is a 3-state lifecycle (building / published / failed) rather than a
 * percentage.
 */
export function deriveBuildState(input: BuildStatusInput | null | undefined): BuildState {
  const deployUrl =
    (typeof input?.last_deploy_url === "string" && input.last_deploy_url) ||
    (typeof input?.site_url === "string" && input.site_url) ||
    (input?.pages_project_name ? `https://${input.pages_project_name}.pages.dev` : null);

  if (buildInProgress(input)) {
    return { state: "building", inProgress: true, label: "Building…", badgeVariant: "warning", at: input?.build_dispatched_at ?? null, deployUrl };
  }

  const buildStatus = input?.last_build_status;
  const at = input?.last_build_at ?? null;

  if (buildStatus === "success") {
    return { state: "published", inProgress: false, label: "Published", badgeVariant: "success", at, deployUrl };
  }
  // The API records "failure"; tolerate "failed" too.
  if (buildStatus === "failure" || buildStatus === "failed") {
    return { state: "failed", inProgress: false, label: "Failed", badgeVariant: "destructive", at, deployUrl };
  }

  return { state: "none", inProgress: false, label: "Not published yet", badgeVariant: "secondary", at: null, deployUrl };
}
